<template>
    <div class="w-full" @mouseover="showVideoOptions = true" @mouseleave="showVideoOptions = false">
        <fullscreen
            class="group relative"
            ref="fullscreen"
            id="fullscreen"
            @change="fullscreenChange">

            <ConfirmBid @confirmbid="bid" />

            <!-- include notifications wrapper here because of the fullscreen hack. sorry about that -->
            <notifications position="top center" width="450px" class="live-notifications">
                <template slot="body" slot-scope="{ item, close }">
                    <div aria-live="assertive" class="wrapper flex items-end px-4 py-6 pointer-events-none sm:p-6 sm:items-start">
                        <div class="w-full flex flex-col items-center space-y-4 sm:items-end">
                            <div class="max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden">
                                <div class="p-4">
                                    <div class="flex items-start">
                                        <div class="flex-shrink-0">
                                            <!-- Heroicon name: outline/check-circle -->
                                            <svg v-if="item.type == 'success'" class="h-6 w-6 text-green-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <svg v-else-if="item.type == 'warn'" class="h-6 w-6 text-orange-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                            </svg>
                                            <svg v-else-if="item.type == 'error'" class="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                                            </svg>
                                            <svg v-else class="h-6 w-6 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <div class="ml-3 w-0 flex-1 pt-0.5">
                                            <p v-if="item.title" class="font-medium text-gray-900 mb-1">{{ item.title }}</p>
                                            <p class="text-gray-500" v-html="item.text" />
                                        </div>
                                        <div class="ml-4 flex-shrink-0 flex">
                                            <button @click="close" class="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                                <span class="sr-only">Close</span>
                                                <!-- Heroicon name: solid/x -->
                                                <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                    <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </notifications>

            <video v-for="stream in auction.ms" :key="stream.stream_name"
                :ref="`videoPlayer_${stream.stream_name}`"
                :id="`videoPlayer_${stream.stream_name}`"
                :class="[
                    'object-fill', 'w-full', 'rounded-lg',
                    auction.cs == stream.name || auction.cs == 'local' ? 'default-video' : 'secondary-video',
                    auction.cs == 'local' ? 'local-video' : '',
                    auction.cs == 'local' && stream.locale == locale ? `locale-${stream.locale}` : null,
                    auction.cs == 'local' && stream.locale != locale ? 'hidden' : null
                ]"
                :poster="`/images/live-${locale}.png`"
                autoplay 
                loop 
                muted 
                controls>
            </video>

            <div v-if="muted" class="absolute flex justify-center items-center inset-0 rounded-lg" style="background: rgba(0, 0, 0, .25); color: #ffffff;">
                <button @click="toggleMute" class="flex flex-col items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    </svg>
                    {{ $t('general.turn_on_sound') }}
                </button>
            </div>

            <div v-else v-show="showVideoOptions  && !fullscreen" class="video-options absolute inset-0 flex flex-col rounded-lg">
                <div class="flex flex-1 justify-center items-center">
                    <button class="flex flex-col justify-center items-center" @click="toggleFullscreen">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="-2 -2 24 24" width="24" height="24" preserveAspectRatio="xMinYMin" class="icon__icon"><path fill="#ffffff" d="M19 0a1 1 0 0 1 1 1v6a1 1 0 0 1-2 0V3.414L11.414 10 18 16.586V13a1 1 0 0 1 2 0v6a1 1 0 0 1-1 1h-6a1 1 0 0 1 0-2h3.586L10 11.414 3.414 18H7a1 1 0 0 1 0 2H1a1 1 0 0 1-1-1v-6a1 1 0 0 1 2 0v3.586L8.586 10 2 3.414V7a1 1 0 1 1-2 0V1a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H3.414L10 8.586 16.586 2H13a1 1 0 0 1 0-2h6z"></path></svg>
                        <span class="mt-1 uppercase text-xs text-white">{{ $t('general.room_view') }}</span>
                    </button>
                </div>

                <div class="flex justify-end">
                    <div class="flex flex-col m-4 justify-center items-center" :class="{'text-red-500': videoVolume == 0, 'text-white': videoVolume > 0}">
                        <circle-slider
                            circleColor="#333333"
                            progressColor="#FFF01F"
                            knobColor="#FFF01F"
                            v-model="videoVolume"
                            :circle-width="5"
                            :progress-width="12"
                            :knob-radius="10"
                        ></circle-slider>
                        <button @click="toggleVideoVolume" class="text-lg flex flex-no-wrap items-center">
                            <svg v-if="videoVolume > 0" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clip-rule="evenodd" />
                            </svg>
                            <svg v-if="videoVolume == 0" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clip-rule="evenodd" />
                            </svg>
                            <span v-if="videoVolume > 0">{{ $t('general.mute_video') }}</span>
                            <span v-if="videoVolume == 0">{{ $t('general.unmute_video') }}</span>
                        </button>
                    </div>
                    <div class="flex flex-col m-4 justify-center items-center" :class="{'text-red-500': soundsVolume == 0, 'text-white': soundsVolume > 0}">
                        <circle-slider
                            circleColor="#333333"
                            progressColor="#FFF01F"
                            knobColor="#FFF01F"
                            v-model="soundsVolume"
                            :circle-width="8"
                            :progress-width="12"
                            :knob-radius="10"
                        ></circle-slider>
                        <button @click="toggleSoundsVolume" class="text-lg flex flex-no-wrap items-center">
                            <svg v-if="soundsVolume > 0" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clip-rule="evenodd" />
                            </svg>
                            <svg v-if="soundsVolume == 0" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clip-rule="evenodd" />
                            </svg>
                            <span v-if="soundsVolume > 0">{{ $t('general.mute_sounds') }}</span>
                            <span v-if="soundsVolume == 0">{{ $t('general.unmute_sounds') }}</span>
                        </button>
                    </div>
                </div>
            </div>

            <div class="overlay fs absolute flex flex-col justify-between bottom-0 left-0 right-0 text-white" v-if="fullscreen">
                <div class="flex paddles justify-center items-center my-8">
                    <TransitionGroup v-if="bids" name="paddles-rise" tag="div">
                        <div v-for="(bid, index) in bids.slice(0, max_paddles)" :key="index">
                            <Paddle :paddle="bid.w" width="75" height="100%" bg-color="#FFF01F" text-color="#000000" />
                        </div>
                    </TransitionGroup>
                    <div v-if="bids.length > max_paddles" class="more ml-4 text-2xl">+ {{ bids.length - max_paddles }}</div>
                </div>
                <div class="flex" style="background-color: rgba(0, 0, 0, .5);">
                    <div class="image p-4">
                        <transition name="fade" mode="out-in">
                            <img :key="lot.id" :src="lot.img80" style="height: 90px;" />
                        </transition>
                    </div>
                    <div class="info relative flex flex-col flex-1 p-4 justify-center">
                        <div class="uppercase">
                            {{ $t('general.lot') }} {{ lot.no }}<span v-if="lot.a">,&nbsp;{{ lot.a }}</span>
                        </div>
                        <div class="">
                            <v-clamp autoresize :max-lines="2">{{ lot.t }}</v-clamp>
                        </div>
                        <div v-if="lot.emin" class="flex flex-no-wrap">
                            <span class="mr-1">{{ $t('general.est') }}:</span>
                            <money-format
                                :value="lot.emin"
                                locale="ro"
                                currency-code="eur"
                                :subunits-value="false"
                                :hide-subunits="true">
                            </money-format>
                            <span class="mx-1">&ndash;</span>
                            <money-format
                                :value="lot.emax"
                                locale="ro"
                                currency-code="eur"
                                :subunits-value="false"
                                :hide-subunits="true">
                            </money-format>
                        </div>
                    </div>
                    <div class="flex p-4 justify-center items-center">
                        <transition name="slide-fade" mode="out-in">
                            <Paddle v-if="winner" :key="winner.w" :paddle="winner.w" bg-color="#FFF01F" text-color="#000000" width="50px" />
                            <Paddle v-else :key="0" :paddle="''" bg-color="#000000" text-color="#f1f1f1" width="50px" />
                        </transition>
                    </div>
                    <div class="flex flex-no-wrap justify-end items-center">
                        <div v-if="lot.p > 0" class="flex flex-col text-center p-4">
                            <div class="text-xs text-white">{{ $t('bids.current') }} (EUR)</div>
                            <div class="text-2xl text-white">
                                <AnimatedNumber :value="lot.p" :precision="0" />
                            </div>
                        </div>
                        <div v-if="lot.k > 0" class="flex flex-col text-center p-4">
                            <div class="text-xs text-white">{{ $t('bids.ask') }} (EUR)</div>
                            <div class="text-2xl text-white">
                                <AnimatedNumber :value="lot.k" :precision="0" />
                            </div>
                        </div>
                    </div>
                    <div class="bid flex justify-center items-center w-64 bg-white text-black">
                        <button v-if="user.id && user.approval_status == 2" :disabled="! canBid" @mouseover="hovering(true)" @mouseleave="hovering(false)" @click="confirmbid" class="flex h-full w-full items-center justify-center text-base font-medium">
                            <span v-if="canBid" class="flex">
                                <span>{{ $t('bids.bid') }}</span>
                                <span class="mx-1">
                                    <AnimatedNumber :value="lot.k" :precision="0" />
                                </span>
                                <span class="uppercase">eur</span>
                            </span>
                            <span v-else-if="isHighestBidder" class="text-black flex flex-col justify-center items-center"><img class="w-6 h-6 mr-2" src="@live/assets/images/clap-black.svg">{{ $t('bids.highest', {'value': winner.p}) }}</a></span>
                            <span v-else-if="isBidder" class="flex">{{ $t('bids.sent', {'value': lot.k}) }}</span>
                            <span v-else-if="isBidding" class="flex">{{ $t('bids.is_bidding') }}</span>
                            <span v-else>{{ $t('bids.wait') }}</span>
                        </button>
                        <a v-else-if="user.id && user.approval_status != 2" :href="`/${locale}/login`" class="w-full inline-flex items-center justify-center text-base font-medium text-white">
                            {{ $t('account.review') }}
                        </a>
                        <a v-else :href="`/${locale}/login`" class="w-full inline-flex items-center justify-center">
                            {{ $t('account.login') }}
                        </a>
                    </div>
                </div>
            </div>
            <div class="overlay absolute text-white flex items-center justify-between p-4 top-0 left-0 right-0" v-if="fullscreen">
                <div class="text-xs">
                    {{ auction.name }}
                </div>
                <div class="flex items-center justify-end">
                    <button @click="toggleFullscreen" class="uppercase text-lg">{{ $t('general.standard_view') }}</button>
                </div>
            </div>
        </fullscreen>
    </div>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import AnimatedNumber from '../global/AnimatedNumber'
import Paddle from '../global/Paddle'
import { Constants } from '../../constants'
import WowzaWebRTCPlay from '../../lib/wowza/WowzaWebRTCPlay.js'
import ConfirmBid from '../modals/ConfirmBid'
import VClamp from 'vue-clamp'

var webRTCAdaptor = [];
var autoRepublishIntervalJob = [];

export default {
    components: {
        AnimatedNumber, Paddle, ConfirmBid, VClamp
    },
    data () {
        return {
            ...Constants,
            fullscreen: false,
            muted: true,
            showVideoOptions: false,
            state: '',
            retryInterval: undefined,
            max_paddles: 8,
            soundsVolume: 50,
            videoVolume: 50,
            lastSoundsVolume: 50,
            lastVideoVolume: 50,
            isBidding: false,
            hover: false,
        }
    },
    computed: {
        ...mapGetters({
            currency: 'getCurrency',
            locale: 'getLocale',
            user: 'getUser',
            auction: 'getAuction',
            lot: 'getLot',
            bids: 'getBids',
            winner: 'getWinner',
            isHighestBidder: 'getIsHighestBidder',
            isWinner: 'getIsWinner',
            isBidder: 'getIsBidder',
        }),
        canBid () {
            if(this.isBidding) return false;
            if(this.lot.s != this.LOT_AUCTION_STATUS_OPEN) return false;
            if(! this.user.id) return false;
            if(this.winner && this.winner.uid === this.user.id) return false;
            if(this.bids.filter(el => el.uid === this.user.id).length > 0) return false;
            return true;
        },
    },
    watch: {
        videoVolume (vol) {
            this.auction.ms.forEach(stream => {
                this.$refs['videoPlayer_' + stream.stream_name][0].volume = vol / 100;
            })
            localStorage.setItem('videoVolume', vol);
        },
        soundsVolume (vol) {
            Howler.volume(vol / 100);
            localStorage.setItem('soundsVolume', vol);
        },
        'auction.cs': function(oa, na) {
            if(oa != na) {
                this.unmuteAll();
            }
        }
    },
    methods: {
        fullscreenChange (fullscreen) {
            this.fullscreen = fullscreen;
        },
        toggleFullscreen () {
            this.$refs['fullscreen'].toggle();
        },
        toggleVideoVolume() {
            if(this.videoVolume > 0) {
                this.lastVideoVolume = this.videoVolume;
                this.videoVolume = 0;
            } else {
                this.videoVolume = this.lastVideoVolume;
            }
        },
        toggleSoundsVolume() {
            if(this.soundsVolume > 0) {
                this.lastSoundsVolume = this.soundsVolume;
                this.soundsVolume = 0;
            } else {
                this.soundsVolume = this.lastSoundsVolume;
            }
        },
        toggleMute () {
            ding.mute(false);
            outbid.mute(false);
            highest.mute(false);
            congrats.mute(false);

            ding.play();
            this.muted = ! this.muted;
            
            Vue.nextTick(() => {
                this.auction.ms.forEach(stream => {
                    if(this.$refs['videoPlayer_' + stream.stream_name][0].classList.contains('hidden')) return;
                    
                    this.$refs['videoPlayer_' + stream.stream_name][0].muted = this.muted;
                })
            })
        },
        unmuteAll() {
            this.muted = false;

            Vue.nextTick(() => {
                this.auction.ms.forEach(stream => {
                    //webRTCAdaptor[stream.stream_name].play();
                    //console.log(webRTCAdaptor[stream.stream_name]);
                    if(this.$refs['videoPlayer_' + stream.stream_name][0].classList.contains('hidden')) {
                        this.$refs['videoPlayer_' + stream.stream_name][0].muted = true;
                    } else {
                        this.$refs['videoPlayer_' + stream.stream_name][0].muted = false;
                    }
                    this.$refs['videoPlayer_' + stream.stream_name][0].play();
                })
            })
        },
        async confirmbid() {
            if(this.user.live_bid_protection_status === 1) {
                this.$modal.show('confirm-bid')
            } else {
                this.bid()
            }
        },
        async bid() {
            this.isBidding = true
            this.$modal.hide('confirm-bid')
            try {
                await this.$store.dispatch('sendBid', {
                    auction_id: this.auction.id,
                    lot_id: this.lot.id,
                    amount: this.lot.k
                })
                this.isBidding = false
            } catch (e) {
                this.isBidding = false
            }
        },
        hovering(v) {
            this.hover = v
        },
        setupWowzaStream(stream) {

            webRTCAdaptor[stream] = new WowzaWebRTCPlay();

            webRTCAdaptor[stream].on({
                onError: this.errorHandler.bind(null, stream),
                onStateChanged: this.stateChangedHandler.bind(null, stream),
            });

            // setup stream name to artmark-default or artmark-default_webrtc
            // this depends on streaming type (using a transcoder or not)
            // wowza = direct webrtc ingest
            // obs = rtmp ingest and opus audio transcoding

            webRTCAdaptor[stream].set({
                videoElementPlay: this.$refs['videoPlayer_' + stream][0],
                sdpURL: 'wss://streaming2.artmark.ro/webrtc-session.json',
                applicationName: 'webrtc',
                streamName: 'artmark-' + stream,
            }).then(() => {
                this.start(stream);
            });
        },
        retryConnection (stream) {
            if (autoRepublishIntervalJob[stream] == undefined || autoRepublishIntervalJob[stream] == null) {
                autoRepublishIntervalJob[stream] = setInterval(() => {
                    this.start(stream)
                }, 5000)
            }
        },
        cancelReconnection(stream) {
            if (autoRepublishIntervalJob[stream] != null) {
                clearInterval(autoRepublishIntervalJob[stream])
                autoRepublishIntervalJob[stream] = null;
            }
        },
        start(stream) {
            webRTCAdaptor[stream].play();
        },
        stop(stream) {
            try {
                webRTCAdaptor[stream].stop();
            } catch (error) {
                console.log(error)
            }
            this.cancelReconnection(stream);
        },
        errorHandler(stream, error) {
            this.retryConnection(stream)
        },
        stateChangedHandler(stream, state) {
            if (state.connectionState == 'connecting') {
                this.cancelReconnection(stream)
            } else if (state.connectionState == 'connected') {
                this.cancelReconnection(stream)
            } else if (state.connectionState == 'disconnected') {
                this.retryConnection(stream)
            } else if (state.connectionState == 'failed') {
                this.retryConnection(stream)
            } else if (state.connectionState == 'stopped') {
                this.retryConnection(stream)
            } else {
                console.log(stream, state)
            }
        },
    },
    mounted () {
        this.videoVolume = parseInt(localStorage.getItem('videoVolume')) || 50;
        this.soundsVolume = parseInt(localStorage.getItem('soundsVolume')) || 50;

        this.auction.ms.forEach(stream => {
            this.cancelReconnection(stream.stream_name);
            this.setupWowzaStream(stream.stream_name);
        });

        /*
        let last_hover = false;
        if(auctionPresenceChannel) {
            setInterval(() => {
                if(this.hover != last_hover) {
                    auctionPresenceChannel.whisper('h', {
                        id: this.user.id,
                        h: this.hover
                    });
                    last_hover = this.hover
                }
            }, 1000)
        }
        */
    },
}
</script>
